import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
  Users, DollarSign, Calendar, TrendingUp, Activity,
  Bed, MessageSquare, Settings, LogOut, ChevronLeft, ChevronRight, PieChart as PieChartIcon,
  BarChart3, Clock, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import './AdminDashboard.css';

// Editorial palette shared with the owner suite — keeps the brand consistent
// across role-specific dashboards. Recharts components consume these directly
// so colour theming lives in one place.
const PALETTE = {
  navy: '#0B1A2C',
  navy700: '#1A3553',
  navy600: '#294B6C',
  gold: '#C8A45D',
  goldSoft: '#D7B776',
  sage: '#7CA98E',
  blush: '#D9886A',
  slate: '#475569',
  slateSoft: '#94A3B8',
  hairline: '#E7E2D8',
  surface: '#FFFFFF',
};

const CHART_PALETTE = [PALETTE.navy, PALETTE.gold, PALETTE.sage, PALETTE.blush, PALETTE.slateSoft];

const AdminDashboard = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  // Mock data - Replace with actual API calls
  const [dashboardData, setDashboardData] = useState({
    totalRevenue: 142500,
    totalBookings: 356,
    occupancyRate: 78.5,
    averageRate: 185.50,
    totalGuests: 892,
    cancelledBookings: 23
  });

  const [revenueData] = useState([
    { month: 'Jan', revenue: 12500, bookings: 45, adr: 175 },
    { month: 'Feb', revenue: 14200, bookings: 52, adr: 180 },
    { month: 'Mar', revenue: 18500, bookings: 68, adr: 185 },
    { month: 'Apr', revenue: 16800, bookings: 61, adr: 178 },
    { month: 'May', revenue: 21000, bookings: 78, adr: 190 },
    { month: 'Jun', revenue: 24500, bookings: 89, adr: 195 },
    { month: 'Jul', revenue: 28000, bookings: 102, adr: 200 },
    { month: 'Aug', revenue: 26500, bookings: 95, adr: 198 },
    { month: 'Sep', revenue: 22000, bookings: 82, adr: 188 },
    { month: 'Oct', revenue: 19500, bookings: 71, adr: 185 },
    { month: 'Nov', revenue: 16000, bookings: 58, adr: 180 },
    { month: 'Dec', revenue: 20000, bookings: 75, adr: 192 }
  ]);

  const [roomTypeData] = useState([
    { name: 'Standard', value: 45, revenue: 35000 },
    { name: 'Deluxe', value: 30, revenue: 42000 },
    { name: 'Suite', value: 15, revenue: 38000 },
    { name: 'Executive', value: 10, revenue: 27500 }
  ]);

  const [dailyOccupancy] = useState([
    { day: 'Mon', occupancy: 82, available: 18 },
    { day: 'Tue', occupancy: 75, available: 25 },
    { day: 'Wed', occupancy: 68, available: 32 },
    { day: 'Thu', occupancy: 85, available: 15 },
    { day: 'Fri', occupancy: 92, available: 8 },
    { day: 'Sat', occupancy: 95, available: 5 },
    { day: 'Sun', occupancy: 88, available: 12 }
  ]);

  const [bookingChannels] = useState([
    { channel: 'Direct', bookings: 125, percentage: 35 },
    { channel: 'Booking.com', bookings: 89, percentage: 25 },
    { channel: 'Expedia', bookings: 71, percentage: 20 },
    { channel: 'Airbnb', bookings: 53, percentage: 15 },
    { channel: 'Others', bookings: 18, percentage: 5 }
  ]);

  const COLORS = CHART_PALETTE;

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Shared Recharts axis/tooltip styling so every chart breathes the same air.
  const axisStyle = { fontSize: 11, fill: PALETTE.slate, fontFamily: 'Inter, sans-serif' };
  const tooltipStyle = {
    background: PALETTE.surface,
    border: `1px solid ${PALETTE.hairline}`,
    borderRadius: 10,
    boxShadow: '0 12px 24px -12px rgba(11, 26, 44, 0.18)',
    fontSize: 12,
    fontFamily: 'Inter, sans-serif',
    color: PALETTE.navy,
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend }) => {
    const trendUp = trend > 0;
    return (
      <div className="metric-card">
        <div className="metric-header">
          <div className="metric-icon">
            <Icon size={20} strokeWidth={1.6} />
          </div>
          {trend !== undefined && trend !== null && (
            <span className={`trend ${trendUp ? 'positive' : 'negative'}`}>
              {trendUp ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="metric-content">
          <h3>{title}</h3>
          <div className="metric-value">{value}</div>
          {subtitle && <p className="metric-subtitle">{subtitle}</p>}
        </div>
      </div>
    );
  };

  const CircularMetric = ({ title, value }) => (
    <div className="circular-metric">
      <div className="circular-progress">
        <CircularProgressbar
          value={value}
          text={`${value}%`}
          styles={buildStyles({
            textSize: '20px',
            pathColor: PALETTE.gold,
            textColor: PALETTE.navy,
            trailColor: PALETTE.hairline,
            pathTransitionDuration: 0.7,
            strokeLinecap: 'round',
          })}
        />
      </div>
      <h4>{title}</h4>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-loading-state">
        <div className="admin-loading-card">
          <div className="admin-spinner" aria-hidden="true" />
          <h3>Preparing your console</h3>
          <p>Aggregating today's performance, channels, and reservations.</p>
        </div>
      </div>
    );
  }

  const adminName = userProfile?.fullName || userProfile?.full_name || userProfile?.displayName || 'Admin';
  const firstName = adminName.split(' ')[0];
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return 'Good evening';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const navItems = [
    { path: '/admin',              Icon: PieChartIcon, label: 'Analytics', active: true },
    { path: '/admin/approvals',    Icon: Clock,        label: 'Hotel approvals' },
    { path: '/admin/hotel',        Icon: Bed,          label: 'Hotel management' },
    { path: '/admin/users',        Icon: Users,        label: 'User management' },
    { path: '/admin/notifications',Icon: MessageSquare,label: 'Notifications' },
    { path: '/iot-management',     Icon: Activity,     label: 'IoT management' },
    { path: '/admin/bookings',     Icon: Calendar,     label: 'Bookings' },
    { path: '/admin/revenue',      Icon: DollarSign,   label: 'Revenue' },
    { path: '/admin/reports',      Icon: BarChart3,    label: 'Reports' },
    { path: '/admin/settings',     Icon: Settings,     label: 'Settings' },
  ];

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Admin Console</span>
              </span>
            )}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Admin navigation">
          {navItems.map(({ path, Icon, label, active }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`nav-item ${active ? 'active' : ''}`}
              title={!sidebarOpen ? label : undefined}
            >
              <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn" title={!sidebarOpen ? 'Sign out' : undefined}>
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <p className="header-eyebrow">{greeting} · {todayLabel}</p>
            <h1>{firstName},</h1>
            <p className="header-lede">Here's how your portfolio is performing this period.</p>
          </div>
          <div className="header-right">
            <div className="period-pill">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="period-selector"
                aria-label="Reporting period"
              >
                <option value="today">Today</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
                <option value="year">This year</option>
              </select>
            </div>
            <div className="user-profile">
              {currentUser?.photoURL ? (
                <img src={currentUser.photoURL} alt={adminName} />
              ) : (
                <span className="user-avatar-fallback">{adminName.charAt(0).toUpperCase()}</span>
              )}
              <span className="user-name">{adminName}</span>
            </div>
          </div>
        </header>

        {/* Key Metrics */}
        <section className="metrics-section">
          <MetricCard
            title="Total revenue"
            value={`$${dashboardData.totalRevenue.toLocaleString()}`}
            subtitle="vs last period"
            icon={DollarSign}
            trend={12.5}
          />
          <MetricCard
            title="Reservations"
            value={dashboardData.totalBookings}
            subtitle={`${dashboardData.cancelledBookings} cancellations`}
            icon={Calendar}
            trend={8.3}
          />
          <MetricCard
            title="Occupancy"
            value={`${dashboardData.occupancyRate}%`}
            subtitle="Period to date"
            icon={Activity}
            trend={5.2}
          />
          <MetricCard
            title="Average daily rate"
            value={`$${dashboardData.averageRate}`}
            subtitle="Per room, per night"
            icon={TrendingUp}
            trend={-2.1}
          />
        </section>

        {/* Circular Metrics */}
        <section className="circular-metrics-section">
          <CircularMetric title="Occupancy rate" value={dashboardData.occupancyRate} />
          <CircularMetric title="Guest satisfaction" value={92} />
          <CircularMetric title="Staff efficiency" value={87} />
          <CircularMetric title="Service quality" value={95} />
        </section>

        {/* Charts Grid */}
        <section className="charts-grid">
          {/* Revenue Trend */}
          <div className="chart-card large">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Performance</span>
                <h3>Revenue trend</h3>
              </div>
              <div className="chart-controls">
                <button className="chart-btn active">Revenue</button>
                <button className="chart-btn">Bookings</button>
                <button className="chart-btn">ADR</button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PALETTE.gold} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={PALETTE.gold} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 6" stroke={PALETTE.hairline} vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={axisStyle} />
                <YAxis tickLine={false} axisLine={false} tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: PALETTE.hairline, strokeWidth: 1 }} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={PALETTE.gold}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Comparison */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Demand</span>
                <h3>Monthly bookings</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 6" stroke={PALETTE.hairline} vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={axisStyle} />
                <YAxis tickLine={false} axisLine={false} tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(11,26,44,0.04)' }} />
                <Bar dataKey="bookings" fill={PALETTE.navy} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Room Type Distribution */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Mix</span>
                <h3>Room type distribution</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={roomTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  innerRadius={56}
                  outerRadius={92}
                  paddingAngle={3}
                  stroke="none"
                  dataKey="value"
                  style={{ fontSize: 11, fontFamily: 'Inter, sans-serif', fill: PALETTE.slate }}
                >
                  {roomTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Occupancy */}
          <div className="chart-card large">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Inventory</span>
                <h3>Weekly occupancy</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyOccupancy} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 6" stroke={PALETTE.hairline} vertical={false} />
                <XAxis dataKey="day" tickLine={false} axisLine={false} tick={axisStyle} />
                <YAxis tickLine={false} axisLine={false} tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(11,26,44,0.04)' }} />
                <Legend
                  wrapperStyle={{ fontSize: 12, fontFamily: 'Inter, sans-serif', color: PALETTE.slate, paddingTop: 12 }}
                  iconType="circle"
                />
                <Bar dataKey="occupancy" name="Occupied" fill={PALETTE.navy} radius={[6, 6, 0, 0]} />
                <Bar dataKey="available" name="Available" fill={PALETTE.gold} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Channels */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Distribution</span>
                <h3>Booking channels</h3>
              </div>
            </div>
            <div className="channel-list">
              {bookingChannels.map((channel, index) => (
                <div key={index} className="channel-item">
                  <div className="channel-info">
                    <span className="channel-name">{channel.channel}</span>
                    <span className="channel-count">{channel.bookings} bookings</span>
                  </div>
                  <div className="channel-bar">
                    <div
                      className="channel-fill"
                      style={{
                        width: `${channel.percentage}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>
                  <span className="channel-percentage">{channel.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* ADR Trend */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <span className="chart-eyebrow">Pricing</span>
                <h3>Average daily rate</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 6" stroke={PALETTE.hairline} vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={axisStyle} />
                <YAxis tickLine={false} axisLine={false} tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: PALETTE.hairline, strokeWidth: 1 }} />
                <Line
                  type="monotone"
                  dataKey="adr"
                  stroke={PALETTE.navy}
                  strokeWidth={2.5}
                  dot={{ fill: PALETTE.gold, stroke: PALETTE.navy, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: PALETTE.gold, stroke: PALETTE.navy, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Recent Activity */}
        <section className="activity-section">
          <div className="activity-card">
            <div className="card-header">
              <div>
                <span className="chart-eyebrow">Live</span>
                <h3>Recent reservations</h3>
              </div>
              <a href="#all" className="card-link">View all →</a>
            </div>
            <div className="activity-list">
              {[
                { who: 'John Doe', what: 'booked Deluxe Room', room: '#205', when: '2 minutes ago', status: 'confirmed', label: 'Confirmed' },
                { who: 'Jane Smith', what: 'checked in to Suite', room: '#301', when: '15 minutes ago', status: 'checked-in', label: 'Checked in' },
                { who: 'Mike Johnson', what: 'cancelled Standard Room', room: '#102', when: '1 hour ago', status: 'cancelled', label: 'Cancelled' },
                { who: 'Sarah Williams', what: 'checked out from Executive', room: '#405', when: '3 hours ago', status: 'checked-out', label: 'Checked out' },
              ].map((a, i) => (
                <div key={i} className="activity-item">
                  <span className="activity-dot" aria-hidden="true" />
                  <div className="activity-details">
                    <p><strong>{a.who}</strong> {a.what} <span className="activity-room">{a.room}</span></p>
                    <span>{a.when}</span>
                  </div>
                  <span className={`status ${a.status}`}>
                    <span className="status-dot" aria-hidden="true" />
                    {a.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="activity-card">
            <div className="card-header">
              <div>
                <span className="chart-eyebrow">Snapshot</span>
                <h3>Today at a glance</h3>
              </div>
            </div>
            <div className="quick-stats">
              {[
                { Icon: Users, value: dashboardData.totalGuests, label: 'Total guests' },
                { Icon: Bed, value: '42/54', label: 'Rooms occupied' },
                { Icon: Calendar, value: '28', label: 'Check-ins today' },
                { Icon: MessageSquare, value: '15', label: 'Pending requests' },
              ].map(({ Icon, value, label }) => (
                <div key={label} className="stat-item">
                  <div className="stat-icon">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <div className="stat-info">
                    <h4>{value}</h4>
                    <p>{label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
